// FLAG:useDocuAi — makes the blinking AI badge's age limit effective.
//
// The backstop itself lives in `invoice-analysis-state.ts`
// (ANALYZING_MAX_MS): once a run is older
// than the limit, `getInvoiceAnalysisState` no longer returns ANALYZING. However, that function is
// evaluated during RENDERING while the limit elapses over TIME. Without a trigger the component
// does not render again — and without another render, the limit is never checked.
//
// That made the emergency brake ineffective in
// exactly the case it was built for: when the terminal
// event is lost (WebSocket interruption, discarded event, stuck run), no Redux update arrives to
// trigger another render either. The tile then stayed on ANALYZING — not until the limit, but until
// the user interacted with something else. This exact behavior was reported as “runs forever”
// (Issue 04c0203b), and merely reducing the limit would not have changed it.
//
// The interval is deliberately 1 second: the visible upper bound is therefore ANALYZING_MAX_MS plus
// at most one cycle, so the promise “blink for no longer than 45 seconds” also holds for what the
// user SEES. The cost is one `setState` per second and only while something is actually running —
// for analyses taking 0.3–7.3 s (measured locally), that is a handful of render cycles. When
// nothing is running, no timer exists.

import { useEffect, useState } from 'react'

/** How often to reevaluate when runs are considered “running”. */
export const ANALYSIS_CAP_TICK_MS = 1000

/**
 * Forces another render while `analyzing` applies
 * so the time-based age limit can take effect.
 *
 * @param analyzing the currently rendered “running” verdict (ANALYZING or RETRYING).
 */
export const useAnalysisBlinkCap = (analyzing: boolean): void => {
  const [, tick] = useState(0)

  useEffect(() => {
    if (!analyzing) return undefined
    const id = setInterval(() => tick(value => value + 1), ANALYSIS_CAP_TICK_MS)
    return () => clearInterval(id)
  }, [analyzing])
}
